// Local-first completions hook.
//
// Completions are an idempotent toggle on the server: POST {habit_id, date}
// flips the bit. Local model mirrors useSickWeeks — only "true" rows live in
// IDB, the React map is a set, and toggle-off deletes the row. The windowed
// fetch (35 days around currentWeekStart for streak math) reconciles into
// IDB, preserving rows whose habit_id is still an unsynced temp id.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { formatLocalDate, getWeekDates, getWeekStart } from '../utils/dateHelpers';
import { calculateStreaks } from '../utils/streakCalculations';
import { logger } from '../utils/logger';
import { useConnectivity } from '../context/ConnectivityContext';
import {
  bulkPut,
  completionKey,
  del as idbDel,
  getAll as idbGetAll,
  getRangeByIndex,
  put as idbPut,
} from '../offline/db';
import { enqueue, isTempId } from '../offline/outbox';

const log = logger.child('useCompletions');

const STORE = 'completions';

const rowsToMap = (rows) => {
  const m = {};
  for (const r of rows) m[r.key] = true;
  return m;
};

export const useCompletions = (apiUrl, user, habits, currentWeekStart, sickWeeks = {}) => {
  const [completions, setCompletions] = useState({});
  const [showConfetti, setShowConfetti] = useState(false);
  const activeFetchKeys = useRef(new Set());
  const { notifyEnqueued, registerIdResolver, registerRejectionHandler } = useConnectivity();

  const streaks = useMemo(
    () => (habits.length > 0 ? calculateStreaks(completions, habits) : {}),
    [completions, habits]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cached = await idbGetAll(STORE);
        if (!cancelled && cached.length > 0) setCompletions(rowsToMap(cached));
      } catch (e) {
        log.warn('hydrate from idb failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchCompletions = useCallback(async (weekStart, force = false) => {
    if (!user || !weekStart) return;
    const requestKey = `${weekStart.getTime()}`;
    if (!force && activeFetchKeys.current.has(requestKey)) return;
    activeFetchKeys.current.add(requestKey);

    const weekDates = [];
    for (let i = 0; i < 35; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() - 28 + i);
      weekDates.push(formatLocalDate(date));
    }
    const startStr = weekDates[0];
    const endStr = weekDates[weekDates.length - 1];

    try {
      const res = await fetchWithRetry(
        `${apiUrl}/api/completions?start=${startStr}&end=${endStr}`,
        { headers: { 'Authorization': `Bearer ${user.token}` }, cache: 'no-cache' }
      );
      if (!res.ok) throw new Error(`Failed to fetch completions (status ${res.status})`);
      const data = await res.json();

      const serverRows = [];
      for (const c of data) {
        const rawDate = c.date || c.completion_date || c.completed_date;
        const dateStr = typeof rawDate === 'string'
          ? rawDate.split('T')[0]
          : formatLocalDate(new Date(rawDate));
        const habitId = c.habit_id ?? c.habitId ?? c.habit;
        if (!habitId || !dateStr) continue;
        serverRows.push({ key: completionKey(habitId, dateStr), habit_id: habitId, date: dateStr });
      }

      // Reconcile the window: drop IDB rows in [startStr,endStr] the server
      // didn't return, but keep rows whose habit is still an unsynced temp id.
      const inWindow = await getRangeByIndex(STORE, 'date', startStr, endStr);
      const serverKeys = new Set(serverRows.map((r) => r.key));
      const stale = inWindow.filter((r) => !serverKeys.has(r.key) && !isTempId(r.habit_id));
      for (const r of stale) await idbDel(STORE, r.key);
      if (serverRows.length > 0) await bulkPut(STORE, serverRows);

      const all = await idbGetAll(STORE);
      setCompletions(rowsToMap(all));
    } catch (error) {
      log.error('fetchCompletions failed', error, {
        method: 'GET',
        url: `${apiUrl}/api/completions`,
        start: startStr,
        end: endStr,
      });
    } finally {
      activeFetchKeys.current.delete(requestKey);
    }
  }, [user, apiUrl]);

  const checkWeeklyGoals = useCallback(() => {
    if (!currentWeekStart) return;
    const weekDates = getWeekDates(currentWeekStart);
    const weekStartStr = formatLocalDate(getWeekStart(currentWeekStart));
    let allGoalsMet = true;
    habits.forEach((habit) => {
      const isSick = sickWeeks[`${habit.id}-${weekStartStr}`];
      if (isSick) return;
      let count = 0;
      weekDates.forEach((date) => {
        if (completions[`${habit.id}-${date}`]) count++;
      });
      if (count < habit.weekly_goal) allGoalsMet = false;
    });
    if (allGoalsMet && habits.length > 0) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);
    }
  }, [habits, completions, currentWeekStart, sickWeeks]);

  const toggleCompletion = useCallback(async (habitId, date) => {
    const key = completionKey(habitId, date);
    const wasCompleted = !!completions[key];
    const nextCompleted = !wasCompleted;

    setCompletions((prev) => {
      const copy = { ...prev };
      if (nextCompleted) copy[key] = true;
      else delete copy[key];
      return copy;
    });

    try {
      if (nextCompleted) {
        await idbPut(STORE, { key, habit_id: habitId, date });
      } else {
        await idbDel(STORE, key);
      }
      await enqueue({
        type: 'completion.toggle',
        payload: { habit_id: habitId, date },
        habitRef: isTempId(habitId) ? habitId : null,
      });
      notifyEnqueued();
      // Confetti fires off the optimistic state — the only path a user sees,
      // since drains happen out of band.
      checkWeeklyGoals();
    } catch (e) {
      log.error('toggleCompletion enqueue failed', e, { habitId, date });
      setCompletions((prev) => {
        const copy = { ...prev };
        if (wasCompleted) copy[key] = true;
        else delete copy[key];
        return copy;
      });
    }
  }, [completions, checkWeeklyGoals, notifyEnqueued]);

  useEffect(() => {
    return registerIdResolver(async (tempId, realId) => {
      try {
        const all = await idbGetAll(STORE);
        const affected = all.filter((r) => r.habit_id === tempId);
        if (affected.length === 0) return;
        for (const r of affected) await idbDel(STORE, r.key);
        const rewritten = affected.map((r) => ({
          key: completionKey(realId, r.date),
          habit_id: realId,
          date: r.date,
        }));
        await bulkPut(STORE, rewritten);
        const fresh = await idbGetAll(STORE);
        setCompletions(rowsToMap(fresh));
      } catch (e) {
        log.warn('idResolver cascade failed', e, { tempId, realId });
      }
    });
  }, [registerIdResolver]);

  useEffect(() => {
    return registerRejectionHandler(async (row, status) => {
      if (row.type !== 'completion.toggle') return;
      const { habit_id, date } = row.payload;
      const key = completionKey(habit_id, date);
      const cached = await idbGetAll(STORE);
      const present = cached.some((r) => r.key === key);
      setCompletions((prev) => {
        const copy = { ...prev };
        if (copy[key]) delete copy[key]; else copy[key] = true;
        return copy;
      });
      if (present) {
        try { await idbDel(STORE, key); } catch (_) { /* ignore */ }
      } else {
        try { await idbPut(STORE, { key, habit_id, date }); } catch (_) { /* ignore */ }
      }
      log.warn('completion.toggle rejected; reverted local', { habit_id, date, status });
    });
  }, [registerRejectionHandler]);

  useEffect(() => {
    if (currentWeekStart && user) fetchCompletions(currentWeekStart);
  }, [currentWeekStart, user, fetchCompletions]);

  return {
    completions,
    setCompletions,
    streaks,
    showConfetti,
    setShowConfetti,
    fetchCompletions,
    toggleCompletion,
    checkWeeklyGoals,
  };
};
